import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchFeeStructures,
  createFeeStructure,
  updateFeeStructure,
  deleteFeeStructure,
  fetchFeePayments,
  recordFeePayment,
  updateFeePayment,
  deleteFeePayment,
  checkReceiptNumber,
  fetchFeeReport,
  initializeFeeAccounts,
  recalculateTermBalances,
  fetchUsers,
  fetchStudentFees,
  fetchSchoolSettings,
  fetchActiveClasses,
  type FeeStructureDto,
  type FeePaymentDto,
  type FeeReportDto,
  type UserDto,
  type StudentFeeAccountDto,
  type SchoolCode,
  type SchoolClassDto,
} from '../api/axios';
import styles from './FeeManagement.module.css';
import { FeeChargesPanel } from './FeeChargesPanel';
import { classLevelWord, formatClassLevelLabel, getMaxClassLevel } from '../utils/classLevelLabel';

type Tab = 'structure' | 'history' | 'report';

interface Props {
  school: string;
  section: Tab;
}

export const FeeManagement: React.FC<Props> = ({ school, section }) => {
  return (
    <div className={styles.container}>
      {section === 'structure' && <FeeStructureTab school={school} />}
      {section === 'history' && <PaymentHistoryTab school={school} />}
      {section === 'report' && <FeeReportTab school={school} />}
    </div>
  );
};

// ─── Fee Structure Setup Tab ─────────────────────────────────────

const FeeStructureTab: React.FC<{ school: string }> = ({ school }) => {
  const [structures, setStructures] = useState<FeeStructureDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  const [editingStructure, setEditingStructure] = useState<FeeStructureDto | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [academicYear, setAcademicYear] = useState(String(new Date().getFullYear()));
  const [term, setTerm] = useState(1);
  const [form, setForm] = useState(1);
  const [targetScope, setTargetScope] = useState<'single' | 'multiple' | 'all'>('single');
  const [selectedForms, setSelectedForms] = useState<number[]>([]);
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterTerm, setFilterTerm] = useState<number | ''>('');
  const [editAcademicYear, setEditAcademicYear] = useState(String(new Date().getFullYear()));
  const [editTerm, setEditTerm] = useState(1);
  const [editForm, setEditForm] = useState(1);
  const [editFeeType, setEditFeeType] = useState<'boarder' | 'day_scholar'>('day_scholar');
  const [editAmount, setEditAmount] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const maxClassLevel = getMaxClassLevel(school);
  const classLevels = Array.from({ length: maxClassLevel }, (_, i) => i + 1);

  useEffect(() => {
    if (form > maxClassLevel) {
      setForm(maxClassLevel);
    }
    setSelectedForms((prev) => prev.filter((f) => f <= maxClassLevel));
  }, [form, maxClassLevel]);

  useEffect(() => {
    setSettingsLoaded(false);
  }, [school]);

  useEffect(() => {
    if (settingsLoaded) return;
    fetchSchoolSettings(school).then(s => {
      setAcademicYear(s.current_academic_year);
      setTerm(s.current_term);
      setFilterYear(s.current_academic_year);
      setFilterTerm(s.current_term);
      setSettingsLoaded(true);
    }).catch(() => setSettingsLoaded(true));
  }, [school, settingsLoaded]);
  const [boarderAmount, setBoarderAmount] = useState('');
  const [dayScholarAmount, setDayScholarAmount] = useState('');
  const [description, setDescription] = useState('');

  const loadStructures = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchFeeStructures({
        school,
        academic_year: filterYear || undefined,
        term: filterTerm || undefined,
      });
      setStructures(data);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message;
      setError(typeof msg === 'string' ? msg : 'Failed to load fee structures');
    } finally {
      setLoading(false);
    }
  }, [school, filterTerm, filterYear]);

  useEffect(() => {
    loadStructures();
  }, [loadStructures]);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    if (!boarderAmount && !dayScholarAmount) {
      setError('Please enter at least one fee amount');
      return;
    }

    try {
      setIsSaving(true);
      let successCount = 0;
      const failedTypes: string[] = [];
      let lastAppliedForms: number[] = [];

      const getTargetPayload = () => {
        if (targetScope === 'all') {
          return { target_scope: 'all' as const };
        }
        if (targetScope === 'multiple') {
          if (selectedForms.length === 0) {
            setError('Please select at least one class for multiple mode');
            return null;
          }
          return { target_scope: 'multiple' as const, forms: selectedForms };
        }
        return { target_scope: 'single' as const, form };
      };

      const targetPayload = getTargetPayload();
      if (!targetPayload) return;

      if (boarderAmount) {
        try {
          const response = await createFeeStructure({
            school,
            academic_year: academicYear,
            term,
            ...targetPayload,
            fee_type: 'boarder',
            amount: parseFloat(boarderAmount),
            description: description || undefined,
          });
          lastAppliedForms = response.applied_forms;
          successCount++;
        } catch {
          failedTypes.push('Boarder');
        }
      }
      if (dayScholarAmount) {
        try {
          const response = await createFeeStructure({
            school,
            academic_year: academicYear,
            term,
            ...targetPayload,
            fee_type: 'day_scholar',
            amount: parseFloat(dayScholarAmount),
            description: description || undefined,
          });
          lastAppliedForms = response.applied_forms;
          successCount++;
        } catch {
          failedTypes.push('Day Scholar');
        }
      }

      if (successCount === 0) {
        setError('Failed to save fee structure entries. Please try again.');
        return;
      }

      setSuccess(
        failedTypes.length > 0
          ? `Saved ${successCount} fee type(s) for ${lastAppliedForms.length || 0} class(es). Failed: ${failedTypes.join(', ')}.`
          : `Fee structure saved successfully for ${lastAppliedForms.length || 0} class(es).`
      );
      setBoarderAmount('');
      setDayScholarAmount('');
      setDescription('');
      setIsSetupOpen(false);
      await loadStructures();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save fee structure');
    } finally {
      setIsSaving(false);
    }
  };

  const openEditModal = (structure: FeeStructureDto) => {
    setError(null);
    setSuccess(null);
    setEditingStructure(structure);
    setEditAcademicYear(structure.academic_year);
    setEditTerm(structure.term);
    setEditForm(structure.form);
    setEditFeeType(structure.fee_type);
    setEditAmount(String(structure.amount));
    setEditDescription(structure.description || '');
  };

  const handleUpdateStructure = async () => {
    if (!editingStructure) return;
    setError(null);
    setSuccess(null);

    if (!editAcademicYear.trim()) {
      setError('Academic year is required');
      return;
    }
    if (!editAmount || Number(editAmount) < 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setIsSaving(true);
      await updateFeeStructure(editingStructure.id, {
        academic_year: editAcademicYear.trim(),
        term: editTerm,
        form: editForm,
        fee_type: editFeeType,
        amount: Number(editAmount),
        description: editDescription || undefined,
      }, school as SchoolCode);
      setEditingStructure(null);
      setSuccess('Fee structure updated successfully');
      await loadStructures();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update fee structure');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteStructure = async (structure: FeeStructureDto) => {
    setError(null);
    setSuccess(null);
    const typeLabel = structure.fee_type === 'boarder' ? 'Boarder' : 'Day Scholar';
    const confirmed = window.confirm(
      `Delete ${typeLabel} fee for ${formatClassLevelLabel(school, structure.form)}, Term ${structure.term}, ${structure.academic_year}? This is permanent.`
    );
    if (!confirmed) return;

    try {
      await deleteFeeStructure(structure.id, school as SchoolCode);
      setSuccess('Fee structure deleted successfully');
      await loadStructures();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to delete fee structure');
    }
  };

  const handleInitializeAccounts = async () => {
    setError(null);
    setSuccess(null);
    try {
      const result = await initializeFeeAccounts({ school, academic_year: academicYear, term });
      const msg = `${result.message} (${result.total_students} total students)`;
      setSuccess(msg);
      window.alert(msg);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to initialize accounts');
    }
  };

  return (
    <div className={styles.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12 }}>
        <h3 className={styles.sectionTitle} style={{ marginBottom: 0 }}>Current Fee Structures</h3>
        <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setIsSetupOpen(true)}>
          Set Up Fee Structure
        </button>
      </div>

      {error && <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>}
      {success && <div className={`${styles.alert} ${styles.alertSuccess}`}>{success}</div>}

      <div className={styles.filtersRow}>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Year</span>
          <input
            type="text"
            className={styles.filterInput}
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            placeholder="e.g. 2026"
            style={{ width: 120 }}
          />
        </div>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Term</span>
          <select
            className={styles.filterSelect}
            value={filterTerm}
            onChange={(e) => setFilterTerm(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">All Terms</option>
            <option value={1}>Term 1</option>
            <option value={2}>Term 2</option>
            <option value={3}>Term 3</option>
          </select>
        </div>
        <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={loadStructures}>
          Refresh
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : structures.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No fee structures found for {school} in the selected filters.</p>
        </div>
      ) : (
        <div className="tableScroll">
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Year</th>
                <th>Term</th>
                <th>{classLevelWord(school)}</th>
                <th>Type</th>
                <th>Amount (MK)</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {structures.map((s) => (
                <tr key={s.id}>
                  <td>{s.academic_year}</td>
                  <td>Term {s.term}</td>
                  <td>{formatClassLevelLabel(school, s.form)}</td>
                  <td>{s.fee_type === 'boarder' ? 'Boarder' : 'Day Scholar'}</td>
                  <td>{parseFloat(s.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                  <td>{s.description || '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className={`${styles.btn} ${styles.btnSecondary}`}
                        onClick={() => openEditModal(s)}
                      >
                        Edit
                      </button>
                      <button
                        className={`${styles.btn} ${styles.btnDanger}`}
                        onClick={() => {
                          void handleDeleteStructure(s);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FeeChargesPanel
        school={school}
        academicYear={filterYear || academicYear}
        term={typeof filterTerm === 'number' ? filterTerm : term}
      />

      {isSetupOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalCard}>
            <div className={styles.modalHeader}>
              <h4 className={styles.modalTitle}>Set Up Fee Structure</h4>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Academic Year</label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={academicYear}
                  onChange={(e) => setAcademicYear(e.target.value)}
                  placeholder="e.g. 2026"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Term</label>
                <select className={styles.formSelect} value={term} onChange={(e) => setTerm(Number(e.target.value))}>
                  <option value={1}>Term 1</option>
                  <option value={2}>Term 2</option>
                  <option value={3}>Term 3</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Apply To</label>
                <select className={styles.formSelect} value={targetScope} onChange={(e) => setTargetScope(e.target.value as 'single' | 'multiple' | 'all')}>
                  <option value="single">Single Class</option>
                  <option value="multiple">Multiple Classes</option>
                  <option value="all">All Classes</option>
                </select>
              </div>
              {targetScope === 'single' && (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{classLevelWord(school)} / Class</label>
                  <select className={styles.formSelect} value={form} onChange={(e) => setForm(Number(e.target.value))}>
                    {classLevels.map((level) => (
                      <option key={level} value={level}>{formatClassLevelLabel(school, level)}</option>
                    ))}
                  </select>
                </div>
              )}
              {targetScope === 'multiple' && (
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{classLevelWord(school)}s / Classes</label>
                  <select
                    className={styles.formSelect}
                    multiple
                    size={Math.min(maxClassLevel, 6)}
                    value={selectedForms.map(String)}
                    onChange={(e) => {
                      const values = Array.from(e.target.selectedOptions).map((option) => Number(option.value));
                      setSelectedForms(values);
                    }}
                  >
                    {classLevels.map((level) => (
                      <option key={level} value={level}>{formatClassLevelLabel(school, level)}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>School</label>
                <input type="text" className={styles.formInput} value={school} disabled />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Boarder Fee (MK)</label>
                <input
                  type="number"
                  className={styles.formInput}
                  value={boarderAmount}
                  onChange={(e) => setBoarderAmount(e.target.value)}
                  placeholder="e.g. 69696.00"
                  step="0.01"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Day Scholar Fee (MK)</label>
                <input
                  type="number"
                  className={styles.formInput}
                  value={dayScholarAmount}
                  onChange={(e) => setDayScholarAmount(e.target.value)}
                  placeholder="e.g. 195900.00"
                  step="0.01"
                />
              </div>
              <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                <label className={styles.formLabel}>Description (optional)</label>
                <textarea
                  className={styles.formTextarea}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional notes about this fee structure..."
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <button className={`${styles.btn} ${styles.btnSuccess}`} onClick={handleInitializeAccounts}>
                Recalculate existing balances for Term {term}
              </button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setIsSetupOpen(false)}>
                  Cancel
                </button>
                <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleSave} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Fee Structure'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingStructure && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalCard}>
            <div className={styles.modalHeader}>
              <h4 className={styles.modalTitle}>Edit Fee Structure</h4>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Academic Year</label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={editAcademicYear}
                  onChange={(e) => setEditAcademicYear(e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Term</label>
                <select className={styles.formSelect} value={editTerm} onChange={(e) => setEditTerm(Number(e.target.value))}>
                  <option value={1}>Term 1</option>
                  <option value={2}>Term 2</option>
                  <option value={3}>Term 3</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{classLevelWord(school)} / Class</label>
                <select className={styles.formSelect} value={editForm} onChange={(e) => setEditForm(Number(e.target.value))}>
                  {classLevels.map((level) => (
                    <option key={level} value={level}>{formatClassLevelLabel(school, level)}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Fee Type</label>
                <select
                  className={styles.formSelect}
                  value={editFeeType}
                  onChange={(e) => setEditFeeType(e.target.value as 'boarder' | 'day_scholar')}
                >
                  <option value="boarder">Boarder</option>
                  <option value="day_scholar">Day Scholar</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Amount (MK)</label>
                <input
                  type="number"
                  className={styles.formInput}
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  step="0.01"
                />
              </div>
              <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                <label className={styles.formLabel}>Description (optional)</label>
                <textarea
                  className={styles.formTextarea}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setEditingStructure(null)}>
                Cancel
              </button>
              <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleUpdateStructure} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Record Payment Tab ──────────────────────────────────────────

const RecordPaymentTab: React.FC<{
  school: string;
  compact?: boolean;
  onRecorded?: () => Promise<void> | void;
  onCancel?: () => void;
}> = ({ school, compact = false, onRecorded, onCancel }) => {
  const [students, setStudents] = useState<UserDto[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<UserDto | null>(null);
  const [studentAccounts, setStudentAccounts] = useState<StudentFeeAccountDto[]>([]);

  const [receiptNumber, setReceiptNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentTerm, setPaymentTerm] = useState(1);
  const [paymentYear, setPaymentYear] = useState(String(new Date().getFullYear()));

  useEffect(() => {
    fetchSchoolSettings(school).then(s => {
      setPaymentTerm(s.current_term);
      setPaymentYear(s.current_academic_year);
    }).catch(() => {});
  }, [school]);
  const [notes, setNotes] = useState('');

  const [receiptStatus, setReceiptStatus] = useState<'idle' | 'checking' | 'exists' | 'ok'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadStudents();
    setSearchTerm('');
    setShowDropdown(false);
    setSelectedStudent(null);
    setStudentAccounts([]);
    setReceiptNumber('');
    setAmount('');
    setNotes('');
    setReceiptStatus('idle');
    setError(null);
    setSuccess(null);
  }, [school]);

  const loadStudents = async () => {
    try {
      const data = await fetchUsers({ role: 'student', school: school as SchoolCode });
      const usersList = (data as any)?.users || data || [];
      setStudents(Array.isArray(usersList) ? usersList : []);
    } catch {
      // silent
    }
  };

  const filteredStudents = students.filter((s) => {
    const q = searchTerm.toLowerCase();
    return (
      s.name?.toLowerCase().includes(q) ||
      (s as any).student_id?.toLowerCase().includes(q) ||
      (s as any).login_id?.toLowerCase().includes(q)
    );
  });

  const handleSelectStudent = async (student: UserDto) => {
    setSelectedStudent(student);
    setSearchTerm(student.name);
    setShowDropdown(false);
    try {
      const data = await fetchStudentFees(student.id, school as SchoolCode);
      setStudentAccounts(data.accounts);
    } catch {
      setStudentAccounts([]);
    }
  };

  const handleCheckReceipt = async () => {
    if (!receiptNumber.trim()) return;
    setReceiptStatus('checking');
    try {
      const result = await checkReceiptNumber(school as SchoolCode, receiptNumber.trim());
      setReceiptStatus(result.exists ? 'exists' : 'ok');
    } catch {
      setReceiptStatus('idle');
    }
  };

  useEffect(() => {
    if (receiptNumber.trim().length > 2) {
      const timer = setTimeout(handleCheckReceipt, 500);
      return () => clearTimeout(timer);
    } else {
      setReceiptStatus('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptNumber]);

  const handleSubmitPayment = async () => {
    setError(null);
    setSuccess(null);

    if (!selectedStudent) {
      setError('Please select a student');
      return;
    }
    if (!receiptNumber.trim()) {
      setError('Please enter a receipt number');
      return;
    }
    if (receiptStatus === 'exists') {
      setError('This receipt number has already been recorded. Cannot submit duplicate receipts.');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    setSubmitting(true);
    try {
      const result = await recordFeePayment({
        student_id: selectedStudent.id,
        receipt_number: receiptNumber.trim(),
        amount: parseFloat(amount),
        payment_date: paymentDate,
        term: paymentTerm,
        academic_year: paymentYear,
        notes: notes || undefined,
      });

      let msg = `Payment of MK${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })} recorded for ${selectedStudent.name}. ` +
        `New balance: MK${parseFloat(result.account.balance).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
      if (result.carry_over_applied) {
        msg += ` | Overpayment of MK${Number(result.carry_over_applied.credit).toLocaleString('en-US', { minimumFractionDigits: 2 })} carried over to Term ${result.carry_over_applied.term} ${result.carry_over_applied.year}.`;
      }
      setSuccess(msg);
      setReceiptNumber('');
      setAmount('');
      setNotes('');
      setReceiptStatus('idle');

      // Refresh student accounts
      const data = await fetchStudentFees(selectedStudent.id, school as SchoolCode);
      setStudentAccounts(data.accounts);
      await onRecorded?.();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Failed to record payment';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.section}>
      {!compact && <h3 className={styles.sectionTitle}>Record Student Payment</h3>}

      {error && <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>}
      {success && <div className={`${styles.alert} ${styles.alertSuccess}`}>{success}</div>}

      <div className={styles.formGrid}>
        <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
          <label className={styles.formLabel}>Search Student</label>
          <div className={styles.studentSearch}>
            <input
              type="text"
              className={styles.formInput}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
                if (!e.target.value) setSelectedStudent(null);
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder="Type student name or ID..."
              style={{ width: '100%' }}
            />
            {showDropdown && searchTerm && filteredStudents.length > 0 && (
              <div className={styles.studentDropdown}>
                {filteredStudents.slice(0, 10).map((s) => (
                  <div
                    key={s.id}
                    className={styles.studentOption}
                    onClick={() => handleSelectStudent(s)}
                  >
                    <span className={styles.studentOptionName}>{s.name}</span>
                    <span className={styles.studentOptionMeta}>
                      {(s as any).student_id || (s as any).login_id} | {formatClassLevelLabel(school, (s as any).form)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Receipt Number</label>
          <input
            type="text"
            className={styles.formInput}
            value={receiptNumber}
            onChange={(e) => setReceiptNumber(e.target.value)}
            placeholder="Enter receipt number"
          />
          <div className={styles.receiptCheck}>
            {receiptStatus === 'checking' && <span style={{ color: '#6b7280', fontSize: 13 }}>Checking...</span>}
            {receiptStatus === 'exists' && <span className={styles.receiptExists}>This receipt already exists!</span>}
            {receiptStatus === 'ok' && <span className={styles.receiptOk}>Receipt number is available</span>}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Amount (MK)</label>
          <input
            type="number"
            className={styles.formInput}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 50000.00"
            step="0.01"
            min="0.01"
          />
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            Overpayments automatically carry over as credit to the next term.
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Payment Date</label>
          <input
            type="date"
            className={styles.formInput}
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Term</label>
          <select className={styles.formSelect} value={paymentTerm} onChange={(e) => setPaymentTerm(Number(e.target.value))}>
            <option value={1}>Term 1</option>
            <option value={2}>Term 2</option>
            <option value={3}>Term 3</option>
          </select>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Academic Year</label>
          <input
            type="text"
            className={styles.formInput}
            value={paymentYear}
            onChange={(e) => setPaymentYear(e.target.value)}
            placeholder="e.g. 2026"
          />
        </div>

        <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
          <label className={styles.formLabel}>Notes (optional)</label>
          <textarea
            className={styles.formTextarea}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional notes about this payment..."
          />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        {compact && (
          <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={onCancel}>
            Cancel
          </button>
        )}
        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={handleSubmitPayment}
          disabled={submitting || receiptStatus === 'exists'}
        >
          {submitting ? 'Recording...' : 'Record Payment'}
        </button>
      </div>

      {selectedStudent && studentAccounts.length > 0 && (
        <div className={styles.balanceDisplay} style={{ marginTop: 24 }}>
          <h4 style={{ marginBottom: 12, fontWeight: 600 }}>
            Current Balances for {selectedStudent.name}
          </h4>
          {studentAccounts.map((acc) => {
            const balance = parseFloat(acc.balance);
            const carryOver = parseFloat(acc.carry_over_credit || '0');
            return (
              <div key={acc.id} className={styles.balanceRow}>
                <span className={styles.balanceLabel}>
                  {acc.academic_year} Term {acc.term}
                </span>
                <span>
                  Fee: MK{parseFloat(acc.total_fee).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  {carryOver > 0 && (
                    <> | <span style={{ color: '#2563eb' }}>Credit from prev term: MK{carryOver.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></>
                  )}
                  {parseFloat(acc.opening_balance || '0') > 0 && (
                    <> | <span>Brought forward: MK{parseFloat(acc.opening_balance || '0').toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></>
                  )}
                  {' | '}
                  Paid: MK{parseFloat(acc.amount_paid).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  {' | '}
                  <strong style={{ color: balance > 0 ? '#dc2626' : '#059669' }}>
                    {balance < 0
                      ? `Overpaid: MK${Math.abs(balance).toLocaleString('en-US', { minimumFractionDigits: 2 })} (carries to next term)`
                      : `Balance: MK${balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                  </strong>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Payment History Tab ─────────────────────────────────────────

const PaymentHistoryTab: React.FC<{ school: string }> = ({ school }) => {
  const [payments, setPayments] = useState<FeePaymentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterTerm, setFilterTerm] = useState<number | ''>('');
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterForm, setFilterForm] = useState<number | ''>('');
  const [classOptions, setClassOptions] = useState<SchoolClassDto[]>([]);
  const [editingPayment, setEditingPayment] = useState<FeePaymentDto | null>(null);
  const [editReceipt, setEditReceipt] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editPaymentDate, setEditPaymentDate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [isRecordPaymentOpen, setIsRecordPaymentOpen] = useState(false);
  const [currentYear, setCurrentYear] = useState('');
  const [currentTerm, setCurrentTerm] = useState<number | null>(null);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    fetchSchoolSettings(school).then(s => {
      setFilterYear(s.current_academic_year);
      setCurrentYear(s.current_academic_year);
      setCurrentTerm(s.current_term);
    }).catch(() => {});
  }, [school]);

  useEffect(() => {
    fetchActiveClasses({ school: school as SchoolCode })
      .then(setClassOptions)
      .catch(() => setClassOptions([]));
  }, [school]);

  const loadPayments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFeePayments({
        school,
        academic_year: filterYear || undefined,
        term: filterTerm || undefined,
        search: search || undefined,
        form: filterForm || undefined,
      });
      setPayments(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [school, filterYear, filterTerm, filterForm, search]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const openEditPayment = (payment: FeePaymentDto) => {
    setActionError(null);
    setEditingPayment(payment);
    setEditReceipt(payment.receipt_number);
    setEditAmount(String(payment.amount));
    setEditPaymentDate(payment.payment_date ? payment.payment_date.slice(0, 10) : '');
    setEditNotes(payment.notes || '');
  };

  const handleSaveEdit = async () => {
    if (!editingPayment) return;
    setActionError(null);

    if (!editReceipt.trim()) {
      setActionError('Receipt number is required.');
      return;
    }
    if (!editAmount || Number(editAmount) <= 0) {
      setActionError('Enter a valid amount.');
      return;
    }
    if (!editPaymentDate) {
      setActionError('Payment date is required.');
      return;
    }

    setEditSubmitting(true);
    try {
      await updateFeePayment(editingPayment.id, {
        receipt_number: editReceipt.trim(),
        amount: Number(editAmount),
        payment_date: editPaymentDate,
        notes: editNotes || undefined,
      }, school as SchoolCode);
      setEditingPayment(null);
      await loadPayments();
    } catch (err: any) {
      setActionError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to update payment');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleRecalculateBalances = async () => {
    if (!currentYear || !currentTerm) {
      setActionError('The current year and term are not available yet.');
      return;
    }

    const ok = window.confirm(
      `Recalculate Term ${currentTerm} ${currentYear} balances from the fee structures already set?\n\nStudents whose boarding status changed this term will be charged the matching boarder or day scholar fee. Payments already recorded stay on the account.`
    );
    if (!ok) return;

    setRecalculating(true);
    setActionError(null);
    setActionSuccess(null);
    try {
      const result = await recalculateTermBalances({
        school,
        academic_year: currentYear,
        term: currentTerm,
      });
      setActionSuccess(result.message);
      await loadPayments();
    } catch (err: any) {
      setActionError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to recalculate balances');
    } finally {
      setRecalculating(false);
    }
  };

  const handleVoidPayment = async (payment: FeePaymentDto) => {
    setActionError(null);
    const ok = window.confirm(
      `Delete payment receipt ${payment.receipt_number} for ${payment.student?.name || 'this student'}?`
    );
    if (!ok) return;

    try {
      await deleteFeePayment(payment.id, school as SchoolCode);
      await loadPayments();
    } catch (err: any) {
      setActionError(err?.response?.data?.error || err?.response?.data?.message || 'Failed to delete payment');
    }
  };

  return (
    <div className={styles.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12 }}>
        <h3 className={styles.sectionTitle} style={{ marginBottom: 0 }}>Collections</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className={`${styles.btn} ${styles.btnSuccess}`}
            onClick={handleRecalculateBalances}
            disabled={recalculating || !currentYear || !currentTerm}
          >
            {recalculating
              ? 'Recalculating...'
              : `Recalculate Term ${currentTerm ?? ''} balances`}
          </button>
          <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => setIsRecordPaymentOpen(true)}>
            Record Payment
          </button>
        </div>
      </div>
      <p style={{ marginTop: -8, marginBottom: 12, color: '#6b7280', fontSize: 14 }}>
        Use <strong>{classLevelWord(school)} / Class</strong> to show only payments from students in that class level.
        Recalculate applies the fee structures already set to Term {currentTerm ?? '—'} {currentYear || ''}, so a boarding change this term updates what each student owes. Payments stay.
      </p>
      {actionError && <div className={`${styles.alert} ${styles.alertError}`}>{actionError}</div>}
      {actionSuccess && <div className={`${styles.alert} ${styles.alertSuccess}`}>{actionSuccess}</div>}

      <div className={styles.filtersRow}>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Search</span>
          <input
            type="text"
            className={styles.filterInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Receipt or student name..."
            style={{ minWidth: 220 }}
          />
        </div>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Year</span>
          <input
            type="text"
            className={styles.filterInput}
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            placeholder="e.g. 2026"
            style={{ width: 100 }}
          />
        </div>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Term</span>
          <select
            className={styles.filterSelect}
            value={filterTerm}
            onChange={(e) => setFilterTerm(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">All Terms</option>
            <option value={1}>Term 1</option>
            <option value={2}>Term 2</option>
            <option value={3}>Term 3</option>
          </select>
        </div>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>{classLevelWord(school)} / Class</span>
          <select
            className={styles.filterSelect}
            value={filterForm}
            onChange={(e) => setFilterForm(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">All forms / classes</option>
            {(classOptions.length > 0
              ? [...classOptions].sort((a, b) => a.level - b.level)
              : Array.from({ length: getMaxClassLevel(school) }, (_, i) => i + 1).map((level) => ({
                  id: -level,
                  name: formatClassLevelLabel(school, level),
                  short_name: null,
                  level,
                  description: null,
                  status: 'active' as const,
                }))
            ).map((c) => (
              <option key={c.id} value={c.level}>
                {c.short_name || c.name || formatClassLevelLabel(school, c.level)}
              </option>
            ))}
          </select>
        </div>
        <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={loadPayments}>
          Refresh
        </button>
      </div>

      {loading ? (
        <p>Loading payments...</p>
      ) : payments.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No payments found for the selected filters.</p>
        </div>
      ) : (
        <div className="tableScroll">
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Receipt #</th>
                <th>Student</th>
                <th>{classLevelWord(school)}</th>
                <th>Amount (MK)</th>
                <th>Amount Remaining / Overpaid (MK)</th>
                <th>Date</th>
                <th>Term</th>
                <th>Year</th>
                <th>Recorded By</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => {
                const balance = Number(p.account_balance ?? '0');
                const owes = balance > 0;
                const signedDisplay = owes ? -Math.abs(balance) : Math.abs(balance);
                return (
                  <tr key={p.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{p.receipt_number}</td>
                    <td>{p.student?.name || `Student #${p.student_id}`}</td>
                    <td>
                      {p.student?.form != null && p.student.form > 0
                        ? formatClassLevelLabel(p.student.school ?? school, p.student.form)
                        : '—'}
                    </td>
                    <td>{parseFloat(p.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td style={{ color: owes ? '#dc2626' : '#059669', fontWeight: 700 }}>
                      {signedDisplay >= 0 ? '+' : ''}
                      {signedDisplay.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td>{new Date(p.payment_date).toLocaleDateString('en-GB')}</td>
                    <td>Term {p.term}</td>
                    <td>{p.academic_year}</td>
                    <td>{p.recorder?.name || '-'}</td>
                    <td>{p.notes || '-'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className={`${styles.btn} ${styles.btnSecondary}`}
                          onClick={() => openEditPayment(p)}
                        >
                          Edit
                        </button>
                        <button
                          className={`${styles.btn} ${styles.btnDanger}`}
                          onClick={() => handleVoidPayment(p)}
                        >
                          Void
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingPayment && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: 16,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 640,
              background: '#fff',
              borderRadius: 14,
              border: '1px solid #e5e7eb',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              padding: 20,
            }}
          >
            <h4 style={{ marginTop: 0, marginBottom: 14 }}>Edit Payment</h4>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Receipt Number</label>
                <input className={styles.formInput} value={editReceipt} onChange={(e) => setEditReceipt(e.target.value)} />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Amount (MK)</label>
                <input
                  className={styles.formInput}
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Payment Date</label>
                <input
                  className={styles.formInput}
                  type="date"
                  value={editPaymentDate}
                  onChange={(e) => setEditPaymentDate(e.target.value)}
                />
              </div>
              <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                <label className={styles.formLabel}>Notes (optional)</label>
                <textarea
                  className={styles.formTextarea}
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                />
              </div>
            </div>
            {actionError && <div className={`${styles.alert} ${styles.alertError}`}>{actionError}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setEditingPayment(null)}>
                Cancel
              </button>
              <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleSaveEdit} disabled={editSubmitting}>
                {editSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isRecordPaymentOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: 16,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 980,
              maxHeight: '90vh',
              overflow: 'auto',
              background: '#fff',
              borderRadius: 16,
              border: '1px solid #e5e7eb',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              padding: 20,
            }}
          >
            <RecordPaymentTab
              school={school}
              compact
              onCancel={() => setIsRecordPaymentOpen(false)}
              onRecorded={async () => {
                setIsRecordPaymentOpen(false);
                await loadPayments();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Fee Report Tab ──────────────────────────────────────────────

const FeeReportTab: React.FC<{ school: string }> = ({ school }) => {
  const [report, setReport] = useState<FeeReportDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterTerm, setFilterTerm] = useState<number | ''>('');

  useEffect(() => {
    fetchSchoolSettings(school).then(s => {
      setFilterYear(s.current_academic_year);
    }).catch(() => {});
  }, [school]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFeeReport({
        school,
        academic_year: filterYear || undefined,
        term: filterTerm || undefined,
      });
      setReport(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [school, filterYear, filterTerm]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const formatMK = (val: number) => `MK${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Fee Collection Report</h3>

      <div className={styles.filtersRow}>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Year</span>
          <input
            type="text"
            className={styles.filterInput}
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            style={{ width: 100 }}
          />
        </div>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>Term</span>
          <select
            className={styles.filterSelect}
            value={filterTerm}
            onChange={(e) => setFilterTerm(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">All Terms</option>
            <option value={1}>Term 1</option>
            <option value={2}>Term 2</option>
            <option value={3}>Term 3</option>
          </select>
        </div>
        <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={loadReport}>
          Refresh
        </button>
      </div>
      <p style={{ marginTop: -4, marginBottom: 12, color: '#6b7280', fontSize: 13 }}>
        Boarding status is term-specific. When <strong>All Terms</strong> is selected, fallback can still appear if any
        included term has no saved status for a student.
      </p>

      {loading ? (
        <p>Loading report...</p>
      ) : report ? (
        <>
          <div className={styles.statsGrid}>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Total Expected</div>
              <div className={`${styles.cardValue} ${styles.cardValueBlue}`}>
                {formatMK(report.total_expected)}
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Total Collected</div>
              <div className={`${styles.cardValue} ${styles.cardValueGreen}`}>
                {formatMK(report.total_collected)}
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Outstanding</div>
              <div className={`${styles.cardValue} ${styles.cardValueRed}`}>
                {formatMK(report.total_outstanding)}
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Students</div>
              <div className={styles.cardValue}>{report.total_accounts}</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Fully Paid</div>
              <div className={`${styles.cardValue} ${styles.cardValueGreen}`}>{report.fully_paid}</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardTitle}>Boarding not set</div>
              <div className={styles.cardValue}>{report.fallback_count}</div>
            </div>
          </div>

              {report.fallback_count > 0 && (
            <div className={`${styles.alert} ${styles.alertError}`} style={{ marginBottom: 12 }}>
              {report.fallback_count} student(s) have no boarder/day scholar setting for this term. Their tuition is 0 until that setting is saved.
            </div>
          )}

          <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Student Fee Summary</h4>
          <div className="tableScroll">
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Student</th>
                  <th>ID</th>
                  <th>{classLevelWord(school)}</th>
                  <th>Accommodation</th>
                  <th>Brought forward</th>
                  <th>Expected (MK)</th>
                  <th>Paid (MK)</th>
                  <th>Outstanding / Overpaid (MK)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {report.students.map((student) => {
                  const outstandingColor = student.outstanding > 0 ? '#dc2626' : '#059669';
                  const signedOutstanding =
                    student.outstanding === 0
                      ? `+${student.outstanding.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                      : student.outstanding < 0
                      ? `+${Math.abs(student.outstanding).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                      : `-${Math.abs(student.outstanding).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
                  const statusLabel =
                    student.status === 'owes' ? 'Owes' : student.status === 'overpaid' ? 'Overpaid' : 'Fully Paid';

                  return (
                    <tr key={student.student_id}>
                      <td style={{ fontWeight: 600 }}>{student.name}</td>
                      <td>{student.admission_id || '-'}</td>
                      <td>{student.form ? formatClassLevelLabel(school, student.form) : '—'}</td>
                      <td>
                        {student.accommodation_type === 'boarder'
                          ? 'Boarder'
                          : student.accommodation_type === 'day_scholar'
                            ? 'Day Scholar'
                            : student.accommodation_type === 'unset'
                              ? 'Not set'
                              : 'Mixed by term'}
                        {student.used_fallback && (
                          <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
                            Tuition is 0 until boarding is set
                          </div>
                        )}
                      </td>
                      <td>{formatMK(student.opening_balance || 0)}</td>
                      <td>{formatMK(student.expected)}</td>
                      <td style={{ color: '#059669' }}>{formatMK(student.paid)}</td>
                      <td style={{ color: outstandingColor, fontWeight: 700 }}>{signedOutstanding}</td>
                      <td style={{ color: outstandingColor, fontWeight: 700 }}>{statusLabel}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className={styles.emptyState}>
          <p>No report data available.</p>
        </div>
      )}
    </div>
  );
};
